import { AvailableCategories, GetChallengeResponse, GetElectionResponse, GetPointResponse, GetTopicResponse, IntegrationException, MapDrawer, MapSetUpOptions } from '../types';
import { LitElement, PropertyValueMap, css, html } from 'lit';
import challengeData, { challengeTypeDisplayName } from '../helpers/state/challenge-data';
import { customElement, property, query, state } from 'lit/decorators';
import { detectMobileViewport, inIframe } from '../helpers/device';
import { electionNominationsAreHappening, electionResultsAreLive, electionVotingIsHappening } from '../helpers/api/election-api';
import { getFromLocalStorage, injectGoogleTagIfConfigured, isDefined, setLinkTag, setLocalStorage, setManifest, setMetaTag } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { Fab } from '@material/mwc-fab';
import { MapComponent } from '../components/map-component';
import { SettingDrawer } from '../components/mobile/setting-drawer';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { fonts } from '../style/variables';
import { getQueryStringParameters } from '../helpers/urls';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('command-center-page')
export class CommandCenterPage extends LitElement {
    amplitude_callsite: string = 'Command Center';



    @query('#map')
    mapComponent!: MapComponent;

    @query('#pointListModal')
    pointListModal?: Dialog;

    @query('#legendModal')
    legendModal!: Dialog;

    @query('#uploadModal')
    uploadModal!: Dialog;

    @query('#signinModalComponent')
    signinModalComponent!: Dialog;

    @query('#addPointModal')
    addPointModal!: Dialog;

    @query('#addpointDrawer')
    addpointDrawer?: SettingDrawer;

    @query('#dateTimeRangeModal')
    dateTimeRangeModal!: Dialog;

    @query('#map')
    mapComponentEl!: MapComponent;

    @query('#getRouteDrawer')
    getRouteDrawer?: SettingDrawer;

    @query('#getRouteModal')
    getRouteModal!: Dialog;

    @state()
    challenge?: GetChallengeResponse;

    @state()
    election?: GetElectionResponse;

    @state()
    hasActivatedChallenge: boolean = false;

    @state()
    showHeader = true;

    @state()
    focusedPoints: GetPointResponse[] = [];

    @state()
    topics: GetTopicResponse[] = [];

    @property({ type: String })
    shareLink: string | undefined;

    @property({ type: Array })
    availableCategories: AvailableCategories[] = [];

    @property({ type: String })
    viewType: string | undefined;

    @state()
    openParticipantProgressModal = false;

    @state()
    qrCodeLink: string | undefined;

    @state()
    mapSetUpOptions?: MapSetUpOptions;

    @state()
    showSearch: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTranslationState = new StateController(this, translationData);
    private readonly bindChallengeState = new StateController(this, challengeData);

    async firstUpdated() {
        await this.startup();
    }

    async connectedCallback() {
        super.connectedCallback(); // If you're extending from a base class that also defines connectedCallback
    }

    protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
        super.updated(_changedProperties);
    }

    private async startup() {
        const queryString = getQueryStringParameters();

        const hideHeader =
            queryString.get('hideheader') === 'true' || queryString.get('hideHeader') === 'true';
        this.showHeader = !hideHeader;

        const categories_array: string[] = queryString.get('categories')?.split(',').map(category => category.toLowerCase()) ?? [];
        const tags_array: string[] = queryString.get('tags')?.split(',').map(tag => tag.toLowerCase()) ?? [];

        this.mapSetUpOptions = {
            zoom: queryString.get('zoom') ? Number(queryString.get('zoom')) : undefined,
            center_lat: queryString.get('lat') ? Number(queryString.get('lat')) : undefined,
            center_lng: queryString.get('lng') ? Number(queryString.get('lng')) : undefined,
            focusPointId: queryString.get('point_id') ?? undefined,
            selectedCategories: categories_array ?? undefined,
            selectedTags: tags_array ?? undefined,
            focusChallengeLocationId: queryString.get('challenge_location_id') ?? undefined,
        };

        const topic_id = queryString.get('topic_id') ?? undefined;
        let local_storage_password: string | undefined;
        if (topic_id) {
            local_storage_password = localStorage.getItem(`${topic_id ?? ''}_password`) ?? undefined;
        }

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            true,
            true,
            topic_id,
            local_storage_password,
            true,
            this.mapComponentEl
        );

        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? fonts.mainFont);
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? fonts.mainFont);
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins,
            );

            await translationData.instantiateTranslations(true, true, true);
        }

        // Set the OpenGraph data dynamically.
        // This is not strictly required, since the redirector currently handles this for bots on the server side.
        try {
            setMetaTag('meta[property="og:title"]', 'content', topicData.topic?.share_settings?.page_title ?? topicData.name);
            setMetaTag('meta[property="og:image"]', 'content', topicData.topic?.share_settings?.page_image ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '');
            setLinkTag('link[rel="canonical"]', 'href', topicData.discoveryLevel !== 'private' ? topicData.topic?.discover_details_link ?? '' : topicData.topic?.cc_read_link ?? '');
            setMetaTag('meta[property="og:description"]', 'content', topicData.topic?.share_settings?.page_description ?? topicData.topic?.description ?? `Community map made by ${topicData.topic?.creator ?? 'a builder'} just for you. Check it out!`);
            if (detectMobileViewport()) {
                setManifest(topicData.name,
                    window.location.href,
                    topicData.topic?.share_settings?.phone_short_name,
                    topicData.topic?.share_settings?.phone_icon_large ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '',
                    topicData.topic?.share_settings?.phone_icon_small ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '',
                    topicData.backgroundColor ?? '#ffffff',
                    topicData.backgroundColor ?? '#ffffff'
                );
                setMetaTag('meta[name="apple-mobile-web-app-capable"]', 'content', 'yes');
                setMetaTag('meta[name="apple-mobile-web-app-status-bar-style"]', 'content', 'black');
                setMetaTag('meta[name="apple-mobile-web-app-title"]', 'content', topicData.topic?.share_settings?.phone_short_name ?? topicData.topic?.name ?? 'Map');
                setMetaTag('meta[name="apple-touch-icon"]', 'content', topicData.topic?.share_settings?.phone_icon_small ?? topicData.topic?.custom_social_media_image ?? topicData.topic?.header_logo ?? '');
                setMetaTag('meta[name="viewport"]', 'content', 'initial-scale=1.0, viewport-fit=cover');
            };
        } catch (e) {
            console.error('Error setting all OpenGraph data', e);
        }

        // Inject Google Tag if configured
        injectGoogleTagIfConfigured(topicData.topic?.share_settings?.google_tag);

        // If this is the first time the user has visited, show them all the options.
        await this.firstTimeVisitorSequence();

        // Grab View Type Text for Analytics
        if (topicData.topic?.is_addpointer === true) {
            this.viewType = 'Collaborator';
        } else if (topicData.isWriter) {
            this.viewType = 'Admin';
        } else {
            this.viewType = 'Viewer';
        }

        logWithAmplitude(
            this.amplitude_callsite,
            `Load ${this.viewType} View`,
            { topic_id: topicData.topicId }
        );

        // instantiate timer if the map has email capture turned on as a setting. if the user is logged in, this will be skipped
        // this should be a 60 second timer, when that happens, email-capture-modal will be opened
        // setTimeout is used instead of setInterval because we only want to run this once

        this.challenge = topicData.challengeHappeningNow;
        this.election = topicData.electionHappeningNow;
    }

    private fabOpenAnimation(fabObj: Fab | undefined) {
        // Admin maps have more objects declared than non-admin
        if (!fabObj) {
            return;
        }
        fabObj.setAttribute('extended', 'extended');
        fabObj.removeAttribute('mini');
        fabObj.animate([{ width: '70px' }, { width: '100px' }, { width: 'auto' }], {
            duration: 100,
        });
    }

    private async firstTimeVisitorSequence() {
        if (getFromLocalStorage('hasVisited') === 'true') {
            return;
        }
        setLocalStorage('hasVisited', 'true');
    }

    private async onClickActivateElection(e: CustomEvent) {
        await this.openElectionVotingModal();

        logWithAmplitude(MapComponent.amplitude_callsite, 'Open Election: Election Click', {
            topic_id: topicData.topicId,
            topic_key: topicData.topicKey,
        });
    }

    private onPointAdd(e: CustomEvent) {
        void this.addpointDrawer?.open();
        if (isDefined(this.addPointModal)) {
            this.addPointModal.open = true;
        }

        logWithAmplitude(MapComponent.amplitude_callsite, 'Open Add Point: Point Click Admin', {
            topic_id: topicData.topicId,
            topic_key: topicData.topicKey,
        });
    }


    private async  openElectionVotingModal() {
        await topicData.mapComponent?.onToggleElection();
    }

    private async openElectionNominationModal() {
        await topicData.mapComponent?.onToggleNomination();
    }

    private switchLanguage(language: string) {
        (window as any).Weglot.switchTo(language);
    }

    render() {
        const challengeIsHappening = isDefined(topicData.challengeHappeningNow);
        const electionIsHappening = isDefined(topicData.electionHappeningNow);
        const isMobile = detectMobileViewport();
        const headerColor = topicData.backgroundColor;
        const buttonBackgroundColor = topicData.textColor;
        const nominationsOn = isDefined(topicData.electionHappeningNow) && electionNominationsAreHappening(topicData.electionHappeningNow);
        return html`
            ${topicData.isReady
                ? html`<div id="container">
                      ${this.showHeader
                          ? html`
                                <map-header>
                                    <div
                                        class="headerPart"
                                        style="--context-menu-text-color: ${topicData.topic
                                            ?.header_text_color ?? 'var(--theme-white-color)'};"
                                    >
                                        ${topicData.electionHappeningNow && electionVotingIsHappening(topicData.electionHappeningNow) && !isMobile ? html`
                                            <form-button class="search-btn" size="md" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                @click=${this.openElectionVotingModal}
                                                >
                                                ${topicData.electionHappeningNow?.point_button_text ?? 'Vote Now'}
                                            </form-button>
                                            ` : ''}
                                        ${nominationsOn && !isMobile ? html`
                                            <form-button class="search-btn" size="md" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                @click=${this.openElectionNominationModal}
                                                >
                                                Nominate
                                            </form-button>
                                        ` : ''}
                                        ${challengeIsHappening && !isMobile ? html`
                                            <form-button class="search-btn" size="md" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                @click=${async () => await this.mapComponentEl?.onClickActivateChallenge()}
                                            >
                                                ${topicData.challengeHappeningNow?.challenge_type ? challengeTypeDisplayName(topicData.challengeHappeningNow) : ''}
                                            </form-button>
                                        ` : ''}
                                        ${topicData.isAddPointer && (!isMobile || inIframe()) && !nominationsOn ? html`
                                            ${inIframe() && isMobile ? html`
                                                <form-button class="search-btn" size="sm" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                    @click=${async () => await this.mapComponentEl?.onPointAdd()}
                                                >
                                                    <mwc-icon aria-hidden="true">add_location</mwc-icon> Add
                                                </form-button>
                                            `
                                            :
                                                html`
                                                <form-button class="search-btn" size="md" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                    @click=${async () => await this.mapComponentEl?.onPointAdd()}
                                                >
                                                    <translated-span>Add ${topicData.topic?.point_name ?? 'Point'}</translated-span>
                                                </form-button>
                                            `}
                                        ` : ''}
                                        ${(topicData.hasExceptions === IntegrationException.TrudiFord || topicData.hasExceptions === IntegrationException.WilliamEScooter) && !isMobile ? html`
                                            <form-button class="search-btn ${this.mapComponentEl?.showDrawToolOptions ? 'hideButton' : ''}" size="md" .backgroundColor="${buttonBackgroundColor}" .textColor="${headerColor}"
                                                @click="${async () => await this.mapComponentEl?.startDrawing(google.maps.drawing.OverlayType.POLYLINE, true)}"
                                            >
                                                ${topicData.hasExceptions === IntegrationException.TrudiFord ? 'Highlight Street': 'Add Shape'}
                                            </form-button>
                                        ` : ''}
                                        ${topicData.topic?.translation_languages && topicData.topic?.translation_languages.length > 0 ? html`
                                            <language-selector></language-selector>
                                        ` : ''}
                                        ${this.showSearch || topicData.hasExceptions === IntegrationException.TheGoSolution || topicData.hasExceptions === IntegrationException.Telco || topicData.hasExceptions === IntegrationException.DefHero ? html`
                                            <proxi-autocomplete
                                                style="max-height: 50px;width: 300px;z-index: 4;color: ${topicData.backgroundColor};"
                                                @on-select-point="${async (e: CustomEvent) => {
                                                    await this.mapComponentEl.directlySetFocusedPoint(e.detail);
                                                }}"
                                            ></proxi-autocomplete>
                                        ` : ''}
                                        ${topicData.topic?.hide_map_info_drawer !== true && !isMobile ? html`
                                            <button
                                                class="header-icon-btn"
                                                aria-label="${topicData.topic?.disable_list_button === true ? 'Show map details' : 'Browse locations'}"
                                                @click=${async () => await this.mapComponentEl?.setMapDrawer(MapDrawer.MapInfo)}
                                            ><mwc-icon aria-hidden="true">manage_search</mwc-icon></button>
                                        ` : ''}
                                        ${topicData.topic?.hide_bookmark_buttons !== true ? html`
                                            <bookmark-map-button
                                                id="bookmarkFab"
                                                .topic=${topicData.topic}
                                            >
                                            </bookmark-map-button>
                                        ` : ''}
                                        <context-menu
                                            type="command-center"
                                            amplitude_callsite=${this.amplitude_callsite}
                                            @on-signin=${() => {
                                                this.signinModalComponent.open = true;
                                            }}
                                        >
                                        </context-menu>
                                    </div>
                                </map-header>
                            `
                          : ''}

                      <div id="main">
                          <map-component
                              id="map"
                              .showOtherDrawerButton=${true}
                              .focus_load_parameters="${this.mapSetUpOptions}"
                              .challengeIsHappening="${challengeIsHappening}"
                              .electionIsHappening="${electionIsHappening}"
                              .showElectionLeaders="${electionResultsAreLive(topicData.electionHappeningNow)}"
                              .hasActivatedChallenge="${this.hasActivatedChallenge}"
                              .election="${this.election}"
                              @open-election=${this.onClickActivateElection}
                              @open-add-point=${this.onPointAdd}
                          >
                          </map-component>
                    </div>
                </div>`
                : ''}
            <mwc-dialog id="signinModalComponent" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.signinModalComponent.close()}">
                    <signin-modal-component
                        slot="body"
                        @close="${() => this.signinModalComponent.close()}"
                    >
                    </signin-modal-component>
                </modal-dialog>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`

            :host {
                touch-action: manipulation;
            }

            .shareModalBody {
                padding: 1.5rem;
            }

            .shareModalTitle {
                font-size: 1.5rem;
                font-weight: 700;
                color: var(--main-primary-color);
                text-align: center;
                margin-top: 1.5rem;
            }

            hr {
                border: 0;
                height: 1px;
                background: var(--theme-border-color);
            }

            .dialogTitle {
                display: block;
                position: sticky;
                top: 0px;
                z-index: 10;
            }

            #bookmarkFab {
                display: flex;
            }

            #shareModal .dialogTitle {
                margin-bottom: 20px;
            }

            .title {
                font-size: 1.2em;
                font-weight: 700;
                text-align: center;
            }

            .search-btn {
                box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
                border-radius: 4px;
                animation: pulse 2s ease-in-out 2;
            }

            @keyframes pulse {
                0%, 100% {
                    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
                }
                50% {
                    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 4px, rgba(60, 64, 67, 0.15) 0px 1px 3px 4px;
                }
            }

            .add {
                --mdc-theme-secondary: var(--main-success-color) !important;
                --mdc-theme-on-secondary: var(--theme-white-color) !important;
            }


            .button {
                margin-top: 1px;
                --mdc-theme-secondary: var(--theme-white-color);
                --mdc-theme-on-secondary: var(--main-primary-color);
            }

            .button.highlighted {
                margin-top: 1px;
                --mdc-theme-secondary: var(--main-primary-color);
                --mdc-theme-on-secondary: var(--theme-white-color);
            }

            .closeButton {
                position: absolute;
                top: 0;
                right: 0;
                padding: 2px;
                line-height: 14px;
                min-width: auto;
            }

            #signinModalComponent {
                --mdc-theme-surface: var(--theme-white-color);
            }

            #actionButtonsContainer {
                left: 15px;
                bottom: 25px;
                position: absolute;
                display: flex;
                flex-direction: column;
                justify-content: space-evenly;
            }

            #container {
                display: flex;
                flex-direction: column;
                height: 100%;
                width: 100%;
                position: absolute;
                top: 0px;
                left: 0px;
                overflow: none;
            }

            .headerPart {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .header-icon-btn {
                background: none;
                border: none;
                padding: 0;
                margin: 0;
                cursor: pointer;
                color: inherit;
                display: inline-flex;
                align-items: center;
            }

            .header-icon-btn:focus-visible {
                outline: 2px solid currentColor;
                outline-offset: 2px;
                border-radius: 4px;
            }

            #main {
                position: relative;
                width: 100%;
                height: 100%;
                margin: 0px;
                overflow: hidden;
            }

            #map {
                min-height: 100%;
                min-width: 100%;
            }

            #challengeParticipantProgressDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }

            .hideButton {
                display: none;
            }

            setting-drawer {
                --bottom-height: 0px;
            }

            @media only screen and (max-width: 550px) {
                #addpointDrawer {
                    --bottom-height: 0px;
                    --bottom-height-half: 0px;
                }
            }
        `;
    }
}

